import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

/**
 * Global hook that listens for new group messages across all groups
 * the user belongs to and shows toast notifications.
 */
export function useGroupNotifications() {
  const { user } = useAuth();
  const groupIdsRef = useRef<string[]>([]);
  const profileCacheRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!user) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setup = async () => {
      // Get all groups the user is a member of or owns
      const [{ data: memberGroups }, { data: ownedGroups }] = await Promise.all([
        supabase.from("bracket_group_members").select("group_id").eq("user_id", user.id),
        supabase.from("bracket_groups").select("id").eq("owner_id", user.id),
      ]);

      const ids = new Set<string>();
      memberGroups?.forEach((g) => ids.add(g.group_id));
      ownedGroups?.forEach((g) => ids.add(g.id));
      groupIdsRef.current = [...ids];

      if (ids.size === 0) return;

      // Fetch group names
      const { data: groups } = await supabase
        .from("bracket_groups")
        .select("id, name")
        .in("id", [...ids]);

      const groupNames: Record<string, string> = {};
      groups?.forEach((g) => { groupNames[g.id] = g.name; });

      // Subscribe to all group messages
      channel = supabase
        .channel("global-group-notifications")
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "group_messages" },
          async (payload) => {
            const msg = payload.new as { id: string; group_id: string; user_id: string; content: string };

            // Only notify for groups we're in and messages not from us
            if (msg.user_id === user.id) return;
            if (!groupIdsRef.current.includes(msg.group_id)) return;

            // Don't notify if user is currently viewing any page scoped to
            // that group — covers /groups/:id, /groups/:id/bracket/:uid,
            // and any future sub-routes.
            if (window.location.pathname.startsWith(`/groups/${msg.group_id}`)) return;

            // Get sender name
            let senderName = profileCacheRef.current[msg.user_id];
            if (!senderName) {
              const { data } = await supabase
                .from("profiles")
                .select("display_name")
                .eq("id", msg.user_id)
                .single();
              senderName = data?.display_name || "Someone";
              profileCacheRef.current[msg.user_id] = senderName;
            }

            const groupName = groupNames[msg.group_id] || "a group";
            const preview = msg.content.length > 60 ? msg.content.slice(0, 60) + "…" : msg.content;

            toast(`💬 ${senderName} in ${groupName}`, {
              description: preview,
              action: {
                label: "View",
                onClick: () => {
                  window.location.href = `/groups/${msg.group_id}`;
                },
              },
              duration: 5000,
            });
          }
        )
        .subscribe();
    };

    setup();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [user]);
}
